import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { FC, useCallback, useEffect, useState } from 'react';

import { fetchLeadEmailReplies, fetchLeadRepliesForLead, markLeadEmailReplyRead } from '../../api/leads';
import { LeadEmailReplySchema } from '../../data/types';
import {
  formatLeadFriendlyTimestamp,
  formatLeadFullTimestamp,
  LEAD_DEFAULT_TIMEZONE,
  wrapLeadMergeTagsInHtml,
} from '../../utils/leadsAdmin';
import { LeadMergeTagText } from './LeadMergeTagText';

type ReplyFilter = 'unread' | 'all';

type LeadEmailRepliesPanelProps = {
  adminEmail: string;
  leadId?: string;
  campaignId?: string;
  compact?: boolean;
  /** Community timezone used to render every reply timestamp. */
  timezone?: string;
  /** Parent-controlled inbox rows (global panel only). */
  inboxReplies?: LeadEmailReplySchema[];
  inboxTotal?: number;
  onRefresh?: (options?: { unread?: boolean }) => void | Promise<void>;
  onUnreadChange?: (count: number) => void;
};

/** Inbox / thread panel for inbound lead email replies. */
export const LeadEmailRepliesPanel: FC<LeadEmailRepliesPanelProps> = ({
  adminEmail,
  leadId,
  campaignId,
  compact = false,
  timezone = LEAD_DEFAULT_TIMEZONE,
  inboxReplies,
  inboxTotal = 0,
  onRefresh,
  onUnreadChange,
}) => {
  const useParentInbox = !leadId && !campaignId && inboxReplies !== undefined;
  const [replyFilter, setReplyFilter] = useState<ReplyFilter>('unread');
  const [replies, setReplies] = useState<LeadEmailReplySchema[]>(useParentInbox ? inboxReplies : []);
  const [loading, setLoading] = useState(!useParentInbox);
  const [loadError, setLoadError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    if (useParentInbox) {
      setReplies(inboxReplies);
      setUnreadTotal(inboxReplies.filter((row) => !row.read_at).length);
      setLoading(false);
      setLoadError('');
    }
  }, [inboxReplies, useParentInbox]);

  const loadReplies = useCallback(async (filter: ReplyFilter = replyFilter) => {
    if (!adminEmail) return;
    const unreadOnly = filter === 'unread';
    if (useParentInbox) {
      await onRefresh?.({ unread: unreadOnly });
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const data = leadId
        ? await fetchLeadRepliesForLead(leadId, adminEmail, { limit: 25 })
        : await fetchLeadEmailReplies(adminEmail, {
            limit: compact ? 10 : 25,
            campaign_id: campaignId,
            ...(unreadOnly ? { unread: true } : {}),
          });
      const items = data?.items ?? [];
      const visible = leadId && unreadOnly ? items.filter((row) => !row.read_at) : items;
      setReplies(visible);
      setUnreadTotal(data?.unread_total ?? items.filter((row) => !row.read_at).length);
      onUnreadChange?.(data?.unread_total ?? items.filter((row) => !row.read_at).length);
    } catch (error) {
      console.error('Failed to load lead email replies', error);
      setReplies([]);
      setUnreadTotal(0);
      setLoadError('Could not load email replies. Check the browser console and API credentials.');
    } finally {
      setLoading(false);
    }
  }, [adminEmail, campaignId, compact, leadId, onRefresh, onUnreadChange, replyFilter, useParentInbox]);

  useEffect(() => {
    if (useParentInbox) return;
    loadReplies(replyFilter);
  }, [loadReplies, replyFilter, useParentInbox]);

  const handleFilterChange = (filter: ReplyFilter) => {
    setReplyFilter(filter);
    if (useParentInbox) {
      loadReplies(filter);
    }
  };

  const handleMarkRead = async (reply: LeadEmailReplySchema) => {
    if (!reply.id || reply.read_at) return;
    await markLeadEmailReplyRead(reply.id, adminEmail);
    await loadReplies(replyFilter);
  };

  const displayTotal = useParentInbox ? inboxTotal : replies.length;

  if (loading && replies.length === 0) {
    return <p className="text-sm text-[#737373] font-open-sans">Loading replies…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-[#DC2626] font-open-sans">{loadError}</p>
        <button
          type="button"
          className="rounded-lg border border-[#E9E9E9] px-3 py-1.5 text-xs font-semibold font-raleway"
          onClick={() => loadReplies(replyFilter)}
        >
          Retry
        </button>
      </div>
    );
  }

  const emptyMessage = leadId
    ? 'No replies from this lead yet.'
    : replyFilter === 'unread'
      ? 'No unread replies.'
      : 'No inbound replies yet.';

  const filterToggle = (
    <div className="inline-flex rounded-lg border border-[#E9E9E9] p-0.5" role="tablist" aria-label="Filter replies">
      <button
        type="button"
        role="tab"
        aria-selected={replyFilter === 'unread'}
        className={`rounded-md px-2.5 py-1 text-xs font-semibold font-raleway ${
          replyFilter === 'unread' ? 'bg-[#013847] text-white' : 'text-[#737373] hover:bg-[#F5F5F5]'
        }`}
        onClick={() => handleFilterChange('unread')}
      >
        Unread
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={replyFilter === 'all'}
        className={`rounded-md px-2.5 py-1 text-xs font-semibold font-raleway ${
          replyFilter === 'all' ? 'bg-[#013847] text-white' : 'text-[#737373] hover:bg-[#F5F5F5]'
        }`}
        onClick={() => handleFilterChange('all')}
      >
        All
      </button>
    </div>
  );

  return (
    <div className={compact ? 'space-y-2' : 'rounded-xl border border-[#E9E9E9] bg-white p-5'}>
      {!compact ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold font-raleway">Email replies</h2>
            <p className="text-xs text-[#737373] font-open-sans">
              {displayTotal} inbound {displayTotal === 1 ? 'reply' : 'replies'}
              {unreadTotal ? ` · ${unreadTotal} unread` : ''}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {filterToggle}
            <button
              type="button"
              className="rounded-lg border border-[#E9E9E9] px-3 py-1.5 text-xs font-semibold font-raleway"
              onClick={() => loadReplies(replyFilter)}
            >
              Refresh
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-2 flex justify-end">{filterToggle}</div>
      )}

      {replies.length === 0 ? (
        <p className="text-sm text-[#737373] font-open-sans">{emptyMessage}</p>
      ) : (
        <ul className="space-y-2">
          {replies.map((reply) => {
            const expanded = expandedId === reply.id;
            const unread = !reply.read_at;
            const receivedLabel = formatLeadFriendlyTimestamp(reply.received_at, timezone);
            const receivedTitle = formatLeadFullTimestamp(reply.received_at, timezone) || undefined;
            return (
              <li
                key={reply.id}
                className={`rounded-md border border-[#E4E9EB]  px-3 py-2 ${
                  unread ? 'border-l-[2px] border-l-[#013847]/50 rounded-l-sm bg-[#eff6f7]/10' : 'bg-white'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="flex min-w-0 items-center gap-2 text-sm font-semibold font-raleway text-[#0B2E38]">
                      {unread ? (
                        <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#013847]/50" />
                      ) : null}
                      <span className="truncate font-open-sans">
                        {reply.business_name || reply.first_name || reply.from_email || 'Lead reply'}
                      </span>
                    </p>
                    <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-[#5F7078] font-open-sans">
                      <span className="truncate">
                        <LeadMergeTagText value={reply.subject} fallback="(no subject)" />
                      </span>
                      <span aria-hidden="true" className="text-[#C4CDD1]">
                        ·
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-[#8A9AA1]" title={receivedTitle}>
                        <ScheduleIcon sx={{ fontSize: 12 }} />
                        {receivedLabel}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {unread ? (
                      <button
                        type="button"
                        aria-label="Mark reply read"
                        className="rounded-md p-1 text-[#013847] hover:bg-[#F4F6F7]"
                        onClick={() => handleMarkRead(reply)}
                      >
                        <MarkEmailReadIcon sx={{ fontSize: 16 }} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      aria-label={expanded ? 'Hide reply' : 'View reply'}
                      className="rounded-md p-1 text-[#5F7078] hover:bg-[#F4F6F7]"
                      onClick={() => setExpandedId(expanded ? null : reply.id ?? null)}
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          transform: expanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </button>
                  </div>
                </div>
                {expanded ? (
                  <div className="mt-2 space-y-2">
                    <div
                      className="rounded border border-[#E4E9EB] bg-[#FBFCFC] p-3 text-xs font-open-sans text-[#2C3E50] prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: wrapLeadMergeTagsInHtml(
                          reply.html_body ||
                            `<pre>${(reply.text_body || '')
                              .replace(/&/g, '&amp;')
                              .replace(/</g, '&lt;')
                              .replace(/>/g, '&gt;')}</pre>`
                        ),
                      }}
                    />
                    <p className="inline-flex items-center gap-1 text-[11px] text-[#8A9AA1] font-open-sans" title={receivedTitle}>
                      <ScheduleIcon sx={{ fontSize: 12 }} />
                      {receivedLabel}
                    </p>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
